import { useState } from "react";
import styles from "./ActionsArrayBuilder.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes, faGripVertical, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import SearchableDropdown from "../SearchableDropdown/SearchableDropdown";
import { useLanguage } from "../../context/LanguageContext";

interface ActionConfig {
  id?: string;
  type: string;
  icon: string;
  target: string | number | null;
  [key: string]: any;
}

interface ActionsArrayBuilderProps {
  value: ActionConfig[];
  onChange: (actions: ActionConfig[]) => void;
  title: string;
}

const ACTION_TYPES = [
  { value: "source_status", label: "sourceStatus" },
  { value: "source_start", label: "sourceStart" },
  { value: "source_stop", label: "sourceStop" },
  { value: "edit", label: "edit" },
  { value: "delete", label: "delete" },
  { value: "view", label: "view" },
  { value: "custom", label: "custom" },
];

const COMMON_ICONS = [
  "circle", "play", "stop", "edit", "trash", "eye", "download", "upload", 
  "plus", "check", "times", "copy", "share", "print", "save", "undo", 
  "refresh", "cog", "user", "bell", "heart", "star", "flag"
];

export default function ActionsArrayBuilder({
  value,
  onChange,
  title,
}: ActionsArrayBuilderProps) {
  const { t } = useLanguage();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newActions = [...value];
    const draggedItem = newActions[draggedIndex];
    newActions.splice(draggedIndex, 1);
    newActions.splice(index, 0, draggedItem);

    onChange(newActions);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleAdd = () => {
    const newAction: ActionConfig = {
      id: `action_${Date.now()}`,
      type: "custom",
      icon: "circle",
      target: "",
    };

    onChange([...value, newAction]);
    setEditingIndex(value.length);
  };

  const handleRemove = (index: number) => {
    const newActions = value.filter((_, i) => i !== index);
    onChange(newActions);
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const handleActionChange = (index: number, updates: Partial<ActionConfig>) => {
    const newActions = [...value];
    newActions[index] = { ...newActions[index], ...updates };
    onChange(newActions);
  };

  const toggleEdit = (index: number) => {
    setEditingIndex(editingIndex === index ? null : index);
  };

  const renderActionPreview = (action: ActionConfig) => {
    return (
      <div className={styles.actionPreview}>
        <div className={styles.previewMain}>
          <span className={styles.actionIcon}>
            <FontAwesomeIcon icon={action.icon as any} />
          </span>
          <span className={styles.actionType}>{action.type}</span>
          {action.target !== null && action.target !== "" && (
            <span className={styles.actionTarget}>→ {action.target}</span>
          )}
        </div>
      </div>
    );
  };

  const renderActionEditor = (action: ActionConfig, index: number) => {
    const translatedActionTypes = ACTION_TYPES.map(type => ({
      value: type.value,
      label: t(type.label)
    }));

    return (
      <div className={styles.editForm}>
        <div className={styles.formRow}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t('type')} *</label>
            <SearchableDropdown
              label=""
              options={translatedActionTypes}
              value={action.type}
              onChange={(v) => handleActionChange(index, { type: v })}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t('target')}</label>
            <input
              type="text"
              className={styles.input}
              value={action.target === null ? "" : String(action.target)}
              onChange={(e) => {
                const val = e.target.value;
                handleActionChange(index, { 
                  target: val === "" ? null : val 
                });
              }}
              placeholder={t('targetPlaceholder')}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t('icon')} *</label>
            <div className={styles.iconSelector}>
              <SearchableDropdown
                label=""
                options={COMMON_ICONS.map(icon => ({ label: icon, value: icon }))}
                value={action.icon}
                onChange={(v) => handleActionChange(index, { icon: v })}
              />
              <div className={styles.iconHelp}>
                {COMMON_ICONS.slice(0, 12).map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    className={styles.iconBtn}
                    onClick={() => handleActionChange(index, { icon })}
                    title={icon}
                  >
                    <FontAwesomeIcon icon={icon as any} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.jsonPreview}>
          <span className={styles.jsonLabel}>JSON:</span>
          <code className={styles.jsonCode}>
            {JSON.stringify(
              {
                type: action.type,
                icon: action.icon,
                target: action.target,
              },
              null,
              2
            )}
          </code>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <button type="button" className={styles.addBtn} onClick={handleAdd}>
          <FontAwesomeIcon icon={faPlus} />
          {t('addAction')}
        </button>
      </div>

      <div className={styles.actionsList}>
        {value.length === 0 ? (
          <div className={styles.emptyState}>
            <p>{t('noActions')}</p>
          </div>
        ) : (
          value.map((action, index) => (
            <div
              key={action.id || index}
              className={`${styles.actionCard} ${
                draggedIndex === index ? styles.dragging : ""
              } ${editingIndex === index ? styles.editing : ""}`}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className={styles.dragHandle}>
                <FontAwesomeIcon icon={faGripVertical} />
              </div>

              <div className={styles.actionContent}>
                {editingIndex === index
                  ? renderActionEditor(action, index)
                  : renderActionPreview(action)}
              </div>

              <div className={styles.actions}>
                {editingIndex === index ? (
                  <>
                    <button
                      type="button"
                      className={styles.editBtn}
                      onClick={() => toggleEdit(index)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      type="button"
                      className={styles.closeBtn}
                      onClick={() => setEditingIndex(null)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className={styles.editBtn}
                      onClick={() => toggleEdit(index)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => handleRemove(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}